import { useNostr } from "@nostrify/react";
import { useQuery } from "@tanstack/react-query";

import { useCurrentUser } from "./useCurrentUser";
import {
  ADMIN_PUBKEY,
  ORDER_RELAYS,
  ORDER_STATUS_KIND,
  ORDER_STATUSES,
  type OrderStatus,
} from "@/lib/cashcraft";

import type { NostrEvent } from "@nostrify/nostrify";

export interface OrderStatusInfo {
  status: OrderStatus;
  note: string;
  updatedAt: number;
}

function parseStatus(event: NostrEvent): OrderStatusInfo | null {
  const orderId = event.tags.find(([n]) => n === "d")?.[1];
  const status = event.tags.find(([n]) => n === "status")?.[1];
  if (!orderId || !status) return null;
  if (!ORDER_STATUSES.includes(status as OrderStatus)) return null;
  return {
    status: status as OrderStatus,
    note: event.content,
    updatedAt: event.created_at,
  };
}

/**
 * Fetches the operator's order-status events and returns a map of
 * order event id -> latest status. Only the operator's own (authored) events
 * are trusted.
 */
export function useOrderStatuses() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const isAdmin = user?.pubkey === ADMIN_PUBKEY;

  return useQuery<Record<string, OrderStatusInfo>>({
    queryKey: ["order-statuses", user?.pubkey ?? "anon"],
    enabled: isAdmin,
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);
      const filter = {
        kinds: [ORDER_STATUS_KIND],
        authors: [ADMIN_PUBKEY],
        limit: 500,
      };

      const results = await Promise.allSettled([
        nostr.group(ORDER_RELAYS).query([filter], { signal }),
        nostr.query([filter], { signal }),
      ]);

      const map: Record<string, OrderStatusInfo> = {};
      for (const r of results) {
        if (r.status !== "fulfilled") continue;
        for (const event of r.value) {
          const orderId = event.tags.find(([n]) => n === "d")?.[1];
          const parsed = parseStatus(event);
          if (!orderId || !parsed) continue;
          const existing = map[orderId];
          if (!existing || parsed.updatedAt > existing.updatedAt) {
            map[orderId] = parsed;
          }
        }
      }
      return map;
    },
  });
}
